import { SFAFObject } from '../types/sfaf.type'
import { zeroPad } from '../utils/functions'

export function pushUnique<T>(arr: any, item: T): void {
  if (Array.isArray(arr) && !arr.includes(item)) arr.push(item)
}

export function encode_freq(
  start_ind: string,
  start_freq: number,
  is_band: boolean,
  end_ind: string,
  end_freq: number,
) {
  let out = ''
  out += start_ind
  out += start_freq
  if (end_ind != null && end_freq != null && is_band) {
    out += '-'
    if (end_ind !== start_ind) {
      out += end_ind
    }
    out += end_freq
  }
  return out
}

export function encode_sfaf_location(
  latDeg: number,
  latMin: number,
  latSec: number,
  longDeg: number,
  longMin: number,
  longSec: number,
) {
  // Convert latitude degrees, minutes, and seconds into a formatted string
  let latDirection = latDeg >= 0 ? 'N' : 'S' // North or South based on the sign of the degrees
  latDeg = Math.abs(latDeg) // Take absolute value of degrees
  let formattedLat = `${zeroPad(latDeg, 2)}${zeroPad(latMin, 2)}${zeroPad(latSec, 2)}${latDirection}`

  // Convert longitude degrees, minutes, and seconds into a formatted string
  let longDirection = longDeg >= 0 ? 'E' : 'W' // East or West based on the sign of the degrees
  longDeg = Math.abs(longDeg) // Take absolute value of degrees
  let formattedLong = `${zeroPad(longDeg, 2)}${zeroPad(longMin, 2)}${zeroPad(longSec, 2)}${longDirection}`

  // Concatenate the latitude and longitude into the final SFAF location format
  return `${formattedLat}${formattedLong}`
}

export function encodeField711(radiusKm: number | null, flightLevelKft: number) {
  if (!radiusKm || !flightLevelKft) {
    return ''
  }
  // Convert radius from kilometers to nautical miles
  const nauticalMiles = radiusKm / 1.852

  // Round the nautical miles to the nearest whole number and format as a three-digit string
  const serviceRange = Math.round(nauticalMiles).toString().padStart(3, '0')

  // Format the flight level as a three-digit string
  const flightLevel = flightLevelKft.toString().padStart(3, '0')

  // Concatenate the service range and flight level
  return `${serviceRange}${flightLevel}`
}

export function encode343(data: any) {
  let retval = ''
  if (data) {
    retval = data
    if (!data.includes('J/F')) {
      retval = 'J/F 12/' + zeroPad('' + data, 5)
    }
  }
  return retval
}

export function encodeM015(irac: string, sps: string) {
  let retval = ''
  if (irac) {
    retval += 'IRAC ' + irac + ','
  }
  if (sps) {
    if (irac) {
      retval += 'SPS-' + sps
    } else {
      //special case for old equipment
      retval += sps
    }
  }
  if (retval.length > 0) {
    retval = 'M015,' + retval
  }
  return retval
}

export function get_sfaf_string(sfaf_obj: any) {
  let sfaf = ''
  for (const [key, value] of Object.entries(sfaf_obj).sort()) {
    if (key === '113' || key === '114' || key === '115') {
      if (key === '113') {
        if (Array.isArray(value) && value.length > 0) {
          //print the first
          sfaf += '113. ' + sfaf_obj['113'][0] + '\n'
          sfaf += '114. ' + sfaf_obj['114'][0] + '\n'
          sfaf += '115. ' + sfaf_obj['115'][0] + '\n'
          for (let i = 1; i < value.length; i++) {
            sfaf += `113/${zeroPad(i + 1, 2)}. ${sfaf_obj['113'][i]}\n`
            sfaf += `114/${zeroPad(i + 1, 2)}. ${sfaf_obj['114'][i]}\n`
            sfaf += `115/${zeroPad(i + 1, 2)}. ${sfaf_obj['115'][i]}\n`
          }
        } else {
          sfaf += `113. ${sfaf_obj['113']}\n`
          sfaf += `114. ${sfaf_obj['114']}\n`
          sfaf += `115. ${sfaf_obj['115']}\n`
        }
      }
    } else if (Array.isArray(value) && value.length > 0) {
      //print the first
      sfaf += key + '. ' + value[0] + '\n'

      // According to Sgt Gaylor,
      // only these fields should have "/02" etc when there is more than once "occurrence".
      // Other fields that allow multiple entries (like 400, 401, and 403) just get repeated.
      const fieldsWithOccurrences = [
        340, 440, 343, 443, 354, 454, 355, 455, 357, 457, 360, 460, 361, 461, 362, 462, 363, 463,
      ].map((x) => `${x}`)
      for (let i = 1; i < value.length; i++) {
        if (key === '520') {
          if (i < 15) {
            //Can this box limit each line to a maximum of 72 characters, maximum of 15 lines? This will ensure SXXI can accept it appropriately.
            sfaf += `${key}. ${value[i]}\n`
          }
        } else if (fieldsWithOccurrences.indexOf(key) >= 0) {
          sfaf += `${key}/${zeroPad(i + 1, 2)}. ${value[i]}\n`
        } else {
          sfaf += `${key}. ${value[i]}\n`
        }
      }
    } else {
      sfaf += `${key}. ${value}\n`
    }
  }
  return sfaf.toUpperCase()
}

export function createBlankSfaf(): SFAFObject {
  return {
    '005': 'U',
    '010': 'N', //always N
    '102': '',
    '105': '',
    '110': '',
    '111': [],
    '113': [],
    '114': [],
    '115': [],
    '130': '',
    '140': '',
    '141': '',
    '144': '',
    '200': '',
    '201': '',
    '202': '',
    '204': '',
    '205': '',
    '206': '',
    '207': '',
    '208': '',
    '209': '',
    '300': '',
    '301': '',
    '303': '',
    '306': '',
    '340': [],
    '341': [],
    '343': [],
    //  "345": "",
    //  "346": "",
    //  "347": "",
    //  "348": "",
    //  "349": "",
    '354': [],
    //"355": "", //Remove
    //"356": [], //Remove
    '357': [],
    '358': [],
    '359': [],
    // "360": [],//Remove
    // "361": [],//Remove
    '362': [],
    '363': [],
    '400': '',
    '401': '',
    '403': '',
    '440': [],
    '443': [],
    '454': [],
    // "456": [],//Remove
    '457': [],
    '458': [],
    '459': [],
    // "460": [],//Remove
    // "461": [],//Remove
    '462': [],
    '463': [],
    '500': '',
    '501': '',
    '502': '',
    // "503": "",//Remove
    '511': '',
    '512': '',
    '520': '',
    '701': '',
    '702': '',
    '711': '',
    '716': '',
    // "801": "",//Remove
    '803': '',
    // "804": "", //Remove
    '910': '',
  }
}
